package com.dignicate.web.ui.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.dignicate.providers.AppGraph
import com.dignicate.web.theme.AppRoot
import com.dignicate.web.ui.seo.applyPageMeta
import com.dignicate.web.ui.components.AboutSection
import com.dignicate.web.ui.components.HeroSection
import com.dignicate.web.ui.components.ProductsSection
import com.dignicate.web.ui.components.TechnologiesSection
import com.dignicate.web.ui.components.ValuesSection
import com.dignicate.web.ui.components.layout.PageLayout
import com.dignicate.web.ui.layout.Breakpoint
import com.dignicate.web.ui.layout.rememberBreakpoint
import com.dignicate.web.viewmodels.MainUiState
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event

@Composable
fun MainScreen() {
    AppRoot { locale, isDark ->
        val appGraph = remember(locale.code) { AppGraph(locale.code) }

        console.log("[MainScreen] Composable called with locale: ${locale.code}, isDark: $isDark")

        var currentRoute by remember { mutableStateOf(window.location.pathname) }

        DisposableEffect(Unit) {
            val listener: (Event) -> Unit = { _ -> currentRoute = window.location.pathname }
            window.addEventListener("popstate", listener)
            onDispose { window.removeEventListener("popstate", listener) }
        }

        val navigate: (String) -> Unit = { path ->
            if (path != currentRoute) {
                window.history.pushState(js("null"), "", path)
                currentRoute = path
            }
        }

        MainScreenContent(
            appGraph = appGraph,
            currentRoute = currentRoute,
            navigate = navigate
        )
    }
}

@Composable
private fun MainScreenContent(
    appGraph: AppGraph,
    currentRoute: String,
    navigate: (String) -> Unit
) {
    console.log("[MainScreenContent] Composable called")

    val viewModel = appGraph.mainViewModel
    val termsViewModel = appGraph.termsViewModel
    val privacyPolicyViewModel = appGraph.privacyPolicyViewModel

    val uiState by viewModel.uiState.collectAsState()

    val breakpoint = rememberBreakpoint()

    DisposableEffect(viewModel) {
        console.log("[MainScreenContent] DisposableEffect - calling onCreated")
        viewModel.onCreated()
        onDispose {
            console.log("[MainScreenContent] DisposableEffect - calling onDispose")
            viewModel.onDispose()
        }
    }

    LaunchedEffect(currentRoute) {
        applyPageMeta(currentRoute)
    }

    PageLayout(
        footerNav = uiState.footerNav,
        navigate = navigate,
        onOpenSettings = { /* ToDo */ }
    ) {
        when (currentRoute) {
            "/company" -> {
                CompanyPage(viewModel)
            }

            "/privacy" -> {
                PrivacyPolicyPage(
                    privacyPolicyViewModel
                )
            }

            "/terms" -> {
                TermsPage(termsViewModel)
            }

            else -> {
                when (breakpoint) {
                    Breakpoint.Desktop -> DesktopLayout(
                        uiState
                    )

                    Breakpoint.Tablet -> TabletLayout(
                        uiState
                    )

                    Breakpoint.Mobile -> MobileLayout(
                        uiState
                    )
                }
            }
        }
    }
}

@Composable
private fun DesktopLayout(uiState: MainUiState) {
    Div({ classes("site-wrapper") }) {
        uiState.hero?.let { hero ->
            HeroSection(content = hero)
        }

        uiState.about?.let { about ->
            AboutSection(content = about)
        }

        uiState.technologies?.let { techs ->
            TechnologiesSection(
                technologies = techs,
                title = uiState.technologiesTitle,
                description = uiState.technologiesDescription
            )
        }

        uiState.products?.let { products ->
            ProductsSection(
                products = products,
                title = uiState.productsTitle,
                description = uiState.productsDescription
            )
        }

        uiState.values?.let { values ->
            ValuesSection(
                values = values,
                title = uiState.valuesTitle,
                description = uiState.valuesDescription
            )
        }
    }
}

@Composable
private fun TabletLayout(uiState: MainUiState) {
    DesktopLayout(uiState)
}

@Composable
private fun MobileLayout(uiState: MainUiState) {
    Div({ classes("site-wrapper-mobile") }) {
        uiState.hero?.let { hero ->
            HeroSection(content = hero)
        }

        uiState.about?.let { about ->
            AboutSection(content = about)
        }

        uiState.technologies?.let { techs ->
            TechnologiesSection(
                technologies = techs,
                title = uiState.technologiesTitle,
                description = uiState.technologiesDescription
            )
        }

        uiState.products?.let { products ->
            ProductsSection(
                products = products,
                title = uiState.productsTitle,
                description = uiState.productsDescription
            )
        }

        uiState.values?.let { values ->
            ValuesSection(
                values = values,
                title = uiState.valuesTitle,
                description = uiState.valuesDescription
            )
        }
    }
}
